import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import {
  COGNITO_USER_POOL_ID,
  COGNITO_CLIENT_ID,
  isAuthConfigured as envAuthConfigured,
} from '../config/env';

export interface AuthUser {
  username: string;
  email?: string;
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

let _pool: CognitoUserPool | null = null;

function getPool(): CognitoUserPool {
  if (_pool) return _pool;
  if (!COGNITO_USER_POOL_ID || !COGNITO_CLIENT_ID) {
    throw new Error('Cognito configuration missing: set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID');
  }
  _pool = new CognitoUserPool({ UserPoolId: COGNITO_USER_POOL_ID, ClientId: COGNITO_CLIENT_ID });
  return _pool;
}

export function signIn(username: string, password: string): Promise<AuthUser> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: username, Pool: getPool() });
    const authDetails = new AuthenticationDetails({ Username: username, Password: password });
    user.authenticateUser(authDetails, {
      onSuccess: (session: CognitoUserSession) => {
        resolve({
          username,
          email: session.getIdToken().payload.email,
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        });
      },
      onFailure: (err: Error) => reject(err),
      newPasswordRequired: () => reject(new Error('New password required — use Cognito console or implement change-password flow')),
    });
  });
}

export function signOut(): void {
  const current = getPool().getCurrentUser();
  if (current) current.signOut();
}

export function getCurrentUser(): Promise<AuthUser | null> {
  return new Promise((resolve) => {
    const current = getPool().getCurrentUser();
    if (!current) return resolve(null);
    current.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve({
        username: current.getUsername(),
        email: session.getIdToken().payload.email,
        accessToken: session.getAccessToken().getJwtToken(),
        idToken: session.getIdToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
      });
    });
  });
}

export function isAuthConfigured(): boolean {
  return envAuthConfigured;
}
